import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../services/api';
import { Loader2, Mail, CheckCircle2, AlertCircle } from 'lucide-react';
import { initializeTokensFromResponse } from '../services/authService';
import { useAuth } from '../contexts/AuthContext';

const VerifyEmail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState<string>(location.state?.email || '');
  const [code, setCode] = useState<string>('');
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [isCodeSent, setIsCodeSent] = useState<boolean>(false);
  const [seconds, setSeconds] = useState<number>(0);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [attempts, setAttempts] = useState<number>(0);
  const MAX_ATTEMPTS = 3;
  const auth = useAuth(); // Get the auth context

  useEffect(() => {
    if (!email) {
      toast.error("No email provided for verification");
      navigate('/signin');
    } else {
      // Automatically send verification code when component mounts
      handleSendCode();
    }
  }, [email, navigate]);

  const handleSendCode = async () => {
    try {
      setIsSending(true);
      setErrorMessage('');
      
      // Use the correct endpoint with query parameter instead of request body
      const response = await api.post(`/auth/resend-otp?email=${encodeURIComponent(email)}`);
      
      setIsCodeSent(true);
      setSeconds(60);
      
      // Start countdown timer
      const timer = setInterval(() => {
        setSeconds((prevSeconds) => {
          if (prevSeconds <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prevSeconds - 1;
        });
      }, 1000);
      
      if (response.data?.message) {
        toast.success(response.data.message);
      } else {
        toast.success('Verification code sent to your email!');
      }
    } catch (error) {
      console.error('Failed to send verification code:', error);
      let errorMsg = 'Failed to send verification code. Please try again.';
      
      if (error.response) {
        console.log('Error response status:', error.response.status);
        console.log('Error response data:', error.response.data);
        
        if (error.response.status === 404) {
          errorMsg = 'Email address not found. Please check your registration.';
        } else if (error.response.data?.message) {
          errorMsg = error.response.data.message;
        }
      }
      
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsSending(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!code.trim()) {
      toast.error('Please enter the verification code');
      return;
    }

    let verificationResponse = null;
    try {
      setIsVerifying(true);
      setErrorMessage('');
      setAttempts(prev => prev + 1);
      
      const response = await api.post('/auth/verify-email', { 
        email, 
        otp: code.trim() 
      });
      
      verificationResponse = response; // Store for use outside try block
      
      if (response.data?.accessToken) {
        // Use the comprehensive token initialization function
        initializeTokensFromResponse(response.data, response.headers);
        
        console.log('Authentication successful, tokens initialized', response.data);
        
        // Explicitly dispatch an event to notify the auth system of the change
        window.dispatchEvent(new Event('tokenChange'));
        
        // Dispatch a custom event with additional info
        window.dispatchEvent(new CustomEvent('authStateChange', { 
          detail: { 
            isAuthenticated: true, 
            userId: response.data.userId 
          }
        }));
        
        toast.success('Email verified successfully! You are now logged in.');
        
        // Clear verification state from localStorage if it exists
        localStorage.removeItem('verification_email');
        
        // Set a verification success state for better UX
        setIsVerifying(false);
        
        // Redirect with a longer delay to ensure auth state is fully updated
        // and to let the user see the success message
        setTimeout(() => {
          // If we have a saved redirect path, go there first
          const redirectPath = localStorage.getItem('redirect_after_login');
          if (redirectPath && 
              redirectPath !== '/login' && 
              redirectPath !== '/signin' && 
              redirectPath !== '/verify-email') {
            localStorage.removeItem('redirect_after_login');
            navigate(redirectPath);
          } else {
            // Otherwise go to profile edit
            navigate('/profile/edit', { replace: true });
          }
        }, 2000);
      } else {
        throw new Error('Verification response missing access token');
      }
    } catch (error) {
      console.error('Email verification failed:', error);
      
      let errorMsg = 'Invalid verification code. Please try again.';
      
      if (error.response) {
        console.log('Error response status:', error.response.status);
        console.log('Error response data:', error.response.data);
        
        if (error.response.status === 400) {
          errorMsg = 'Invalid or expired verification code.';
        } else if (error.response.data?.message) {
          errorMsg = error.response.data.message;
        }
      }
      
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
      
      // Handle max attempts
      if (attempts >= MAX_ATTEMPTS - 1) {
        toast.error('Maximum verification attempts reached. Please request a new code.');
        setIsCodeSent(false);
        setSeconds(0);
        setAttempts(0);
      }
    } finally {
      // Only set verifying to false if we had an error
      // For success case, we've already set it above
      if (!verificationResponse?.data?.accessToken) {
        setIsVerifying(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="text-center mb-8">
            <Mail className="mx-auto h-12 w-12 text-yellow-400" />
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              Verify your email
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              We've sent a verification code to<br />
              <span className="font-medium text-gray-900">{email}</span>
            </p>
          </div>

          {errorMessage && (
            <div className="mb-4 p-4 rounded-md bg-red-50 border border-red-200">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <p className="text-sm text-red-700">{errorMessage}</p>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-6">
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-gray-700">
                Verification Code
              </label>
              <div className="mt-1">
                <input
                  id="code"
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-yellow-500 focus:border-yellow-500 sm:text-sm"
                  placeholder="Enter 6-digit code"
                  maxLength={6}
                />
              </div>
            </div>

            <div>
              <button
                onClick={handleVerifyCode}
                disabled={isVerifying || !isCodeSent || code.length !== 6}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {isVerifying ? (
                  <>
                    <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                    Verifying...
                  </>
                ) : (
                  'Verify Email'
                )}
              </button>
            </div>

            <div className="text-center">
              <button
                onClick={handleSendCode}
                disabled={isSending || seconds > 0}
                className="text-sm text-yellow-600 hover:text-yellow-500 disabled:text-gray-400"
              >
                {isSending ? (
                  <>
                    <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4 inline" />
                    Sending...
                  </>
                ) : seconds > 0 ? (
                  `Resend code in ${seconds}s`
                ) : (
                  'Resend verification code'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
